import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTimePeriod, TimezonePreferences, DEFAULT_TIMEZONE_PREFERENCES } from '../utils/timezone';
import persistenceService from '../utils/persistenceService';
import { calculateTextAnimationDuration } from '../config/ui';
import AnimatedText from './onboarding/AnimatedText';
import ErrorBoundary from './ErrorBoundary';
import MoodSlider from './MoodSlider';
import MorningCheckInComplete from './MorningCheckInComplete';
import { useToastContext } from '../contexts/ToastContext';
import { getUserName } from '../utils/userProfile';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Constants for consistent timing
const TIMEOUTS = {
  BUTTON_SHOW_DELAY: 1000,
  MESSAGE_ANIMATION: 500,
  FOCUS_DELAY: 100,
  STEP_TRANSITION: 300,
  RESPONSE_DELAY: 800
} as const;

const LIMITS = {
  INPUT_MAX_LENGTH: 500
} as const;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface CheckInResponse {
  // Morning responses
  moodRating?: number;
  potentialRating?: number;
  primaryGoal?: string;
  energyMotivation?: number;
  additionalThoughts?: string; // Used for both morning and evening
  // Evening responses
  progressRating?: number;
  goalOutcome?: string;
  biggestWin?: string;
  obstacle?: string;
  tomorrowPriority?: string;
}

// Get personalized questions based on user's onboarding priority
const getPersonalizedPriority = (): string => {
  const onboardingAnswers = localStorage.getItem('onboardingAnswers');
  let priority = 'your growth goal';
  
  if (onboardingAnswers) {
    try {
      const answers = JSON.parse(onboardingAnswers);
      priority = (answers.priority || 'your growth goal').toLowerCase();
    } catch (error) {
      console.error('Error parsing onboarding answers:', error);
    }
  }
  
  return priority;
};

// Helper function to create journal entry content from check-in responses
const createCheckInEntryContent = (
  responses: CheckInResponse, 
  type: 'morning' | 'evening',
  questions: any[]
): string => {
  const sections: string[] = [];
  
  if (type === 'morning') {
    sections.push(`🌅 **Morning Check-in**\n`);
    if (responses.moodRating) {
      const moodLabels = { 1: 'Struggling', 2: 'Somewhat Bad', 3: 'Doing Ok', 4: 'Pretty Good', 5: 'Awesome' };
      sections.push(`**Current Mood:** ${moodLabels[responses.moodRating as keyof typeof moodLabels]}`);
    }
    if (responses.potentialRating) {
      sections.push(`**Today's Potential:** ${responses.potentialRating}/10`);
    }
    if (responses.primaryGoal) {
      sections.push(`**Primary Goal:** ${responses.primaryGoal}`);
    }
    if (responses.energyMotivation) {
      sections.push(`**Energy & Motivation:** ${responses.energyMotivation}/10`);
    }
  } else {
    sections.push(`🌙 **Evening Reflection**\n`);
    if (responses.progressRating) {
      sections.push(`**Overall Progress:** ${responses.progressRating}/10`);
    }
    if (responses.goalOutcome) {
      sections.push(`**Goal Outcome:** ${responses.goalOutcome}`);
    }
    if (responses.biggestWin) {
      sections.push(`**Biggest Win:** ${responses.biggestWin}`);
    }
    if (responses.obstacle) {
      sections.push(`**Obstacles Navigated:** ${responses.obstacle}`);
    }
    if (responses.tomorrowPriority) {
      sections.push(`**Tomorrow's Priority:** ${responses.tomorrowPriority}`);
    }
  }
  
  return sections.join('\n\n');
};

// Helper function to get formatted responses for journal entry
const getFormattedResponses = (
  responses: CheckInResponse,
  questions: any[]
): string[] => {
  return questions.map(q => {
    const value = responses[q.field as keyof CheckInResponse];
    if (typeof value === 'number') {
      return `${value}/10`;
    }
    return String(value || 'No response');
  });
};

// Morning fill-in-blank questions that rotate daily
const getMorningFillInQuestion = () => {
  const questions = [
    "Today, my most important focus is ___________",
    "Today, I will make progress by ___________", 
    "The one thing that would make today successful is ___________",
    "Today, my priority is ___________"
  ];
  
  // Rotate based on current date (same question all day)
  const today = new Date();
  const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  const questionIndex = dayOfYear % questions.length;
  
  return questions[questionIndex];
};

// Morning questions configuration
const getMorningQuestions = () => {
  return [
    {
      id: 'morning-mood',
      type: 'mood' as const,
      content: 'How are you feeling right now?',
      field: 'moodRating' as const,
      min: 1,
      max: 5,
      lowLabel: 'Struggling',
      highLabel: 'Awesome'
    },
    {
      id: 'morning-goal',
      type: 'fill-in-blank' as const,
      content: '',
      field: 'primaryGoal' as const,
      placeholder: 'Write...',
      fillInText: getMorningFillInQuestion()
    },
    {
      id: 'morning-journal',
      type: 'text' as const,
      content: 'Tell me more about that...',
      field: 'additionalThoughts' as const,
      placeholder: 'Write...'
    }
  ];
};

// Evening questions configuration
const getEveningQuestions = () => {
  return [
    {
      id: 'evening-progress',
      type: 'rating' as const,
      content: 'Rate today\'s overall progress',
      field: 'progressRating' as const,
      min: 1,
      max: 10,
      lowLabel: 'Low',
      highLabel: 'High'
    },
    {
      id: 'evening-goal-tomorrow',
      type: 'fill-in-blank' as const,
      content: '', // Empty content since we'll render the interactive sentence instead
      fields: ['goalOutcome', 'tomorrowPriority'] as const,
      placeholders: ['today went...', "tomorrow's priority..."]
    },
    {
      id: 'evening-choice',
      type: 'choice' as const,
      content: 'Want to keep reflecting or gently wrap up for today?',
      choices: [
        { id: 'push-further', label: 'Push Further', action: 'continue' },
        { id: 'finish-log', label: 'Finish Log', action: 'complete' }
      ]
    },
    {
      id: 'evening-journal',
      type: 'text' as const,
      content: 'Share any additional thoughts...',
      field: 'additionalThoughts' as const,
      placeholder: 'Write...'
    }
  ];
};

// AI response generation for journal entries
const generateAIResponse = async (journalEntry: string, responses: CheckInResponse): Promise<string> => {
  
  try {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['x-auth-token'] = token;
    }
    
    const accessGranted = localStorage.getItem('thinkmate_access_granted');
    if (accessGranted === 'true') {
      headers['x-access-granted'] = 'true';
    }

    const response = await fetch(`${API_URL}/api/ai/journal-response`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        journalEntry,
        checkInResponses: responses,
        context: 'evening-reflection'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to generate AI response');
    }

    const data = await response.json();
    return data.response || generateContextualFallback(journalEntry);
  } catch (error) {
    console.error('AI response generation error:', error);
    return generateContextualFallback(journalEntry);
  }
};

// Generate contextual fallback responses based on user input quality
const generateContextualFallback = (userInput: string): string => {
  const { classifyResponse } = require('../utils/responseValidation');
  const responseType = classifyResponse(userInput);
  
  const fallbackResponses = {
    vague: [
      "I'd love to understand more about what you're thinking.\n\nCan you share a bit more detail?",
      "It sounds like you have something on your mind.\n\nWhat's coming up for you right now?",
      "I'm here to listen.\n\nWhat would be helpful to explore together?"
    ],
    uncertain: [
      "It's okay to feel unsure.\n\nWhat's been on your mind that's causing this uncertainty?",
      "Uncertainty can be uncomfortable.\n\nWhat questions are you sitting with right now?",
      "Sometimes not knowing is the starting point.\n\nWhat would help you gain some clarity?"
    ],
    brief: [
      "Thanks for sharing that.\n\nWhat else is on your mind about today?",
      "I appreciate you taking time to reflect.\n\nWhat stands out most from your day?",
      "That's a start.\n\nWhat other thoughts or feelings are coming up for you?"
    ],
    detailed: [
      "Thank you for sharing so thoughtfully.\n\nWhat resonates most with you from what you just wrote?",
      "I can see you've given this real thought.\n\nWhat insights are emerging for you?",
      "Your reflection shows a lot of awareness.\n\nWhat would you like to explore further?"
    ],
    clear: [
      "That's really clear and insightful.\n\nHow are you feeling about what you've shared?",
      "You've captured that well.\n\nWhat would be most helpful to focus on next?",
      "I appreciate your honesty and clarity.\n\nWhat's your next step from here?"
    ]
  };
  
  const responses = fallbackResponses[responseType] || fallbackResponses.brief;
  return responses[Math.floor(Math.random() * responses.length)];
};

const CheckInChat: React.FC = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [responses, setResponses] = useState<CheckInResponse>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkInType, setCheckInType] = useState<'morning' | 'evening'>('morning');
  const [, setTimezonePreferences] = useState<TimezonePreferences>(DEFAULT_TIMEZONE_PREFERENCES);
  const [showRatingOptions, setShowRatingOptions] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number>(4);
  const [startTime] = useState(Date.now());
  const [isLoadingValidation, setIsLoadingValidation] = useState(true);
  const [showCompletion, setShowCompletion] = useState(false);
  const [momentumDays, setMomentumDays] = useState(1);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToastContext();

  // Determine check-in type and questions based on time
  useEffect(() => {
    const loadCheckInData = async () => {
      let shouldShowInterface = true;
      
      try {
        // Load timezone preferences
        const preferences = await persistenceService.getPreferences();
        if (preferences.timezone) {
          setTimezonePreferences(preferences.timezone);
        }

        // Determine check-in type based on time
        const currentTime = new Date();
        const timePeriod = getTimePeriod(currentTime, preferences.timezone || DEFAULT_TIMEZONE_PREFERENCES);
        
        const determinedType = (timePeriod === 'morning' || timePeriod === 'afternoon') ? 'morning' : 'evening';
        setCheckInType(determinedType);

        // Check if this check-in type is already completed today
        const token = localStorage.getItem('token');
        if (token) {
          const headers: Record<string, string> = {
            'x-auth-token': token
          };
          
          const accessGranted = localStorage.getItem('thinkmate_access_granted');
          if (accessGranted === 'true') {
            headers['x-access-granted'] = 'true';
          }
          
          const response = await fetch(`${API_URL}/api/daily-checkins/today`, {
            headers
          });
          
          if (response.ok) {
            const data = await response.json();
            
            // Check if the current check-in type is already completed
            const isAlreadyCompleted = determinedType === 'morning' ? data.morning : data.evening;
            
            // TEMPORARILY DISABLED FOR TESTING - Allow multiple check-ins
            /*
            if (isAlreadyCompleted) {
              console.log(`🚫 ${determinedType} check-in already completed - redirecting`);
              showError(
                `${determinedType === 'morning' ? 'Morning' : 'Evening'} check-in already completed!`,
                `You've already completed your ${determinedType} check-in for today. Great job staying consistent!`
              );
              
              // Don't show interface, keep loading
              shouldShowInterface = false;
              
              // Redirect back to dashboard after showing error
              setTimeout(() => {
                navigate('/dashboard');
              }, 3000);
              
              return;
            }
            */
            console.log(`🧪 TESTING MODE: Allowing multiple ${determinedType} check-ins`);
          }
        }

        // Set default ratings
        setResponses({
          moodRating: 4, // Default to "Good" for morning mood
          potentialRating: 5,
          energyMotivation: 5,
          progressRating: 5
        });
        setSelectedRating(4); // Default mood to "Good"

      } catch (error) {
        console.error('Error loading check-in data:', error);
        showError('Error loading check-in', 'Please try again');
        shouldShowInterface = false;
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } finally {
        // Only show interface if validation passed
        if (shouldShowInterface) {
          setIsLoadingValidation(false);
        }
        // If shouldShowInterface is false, keep loading state to prevent flash
      }
    };

    loadCheckInData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const questions = useMemo(() => 
    checkInType === 'morning' ? getMorningQuestions() : getEveningQuestions(), 
    [checkInType]
  );
  const currentQuestion = questions[currentQuestionIndex];
  const isComplete = currentQuestionIndex >= questions.length;

  // Initialize first question only after validation completes
  useEffect(() => {
    if (!isLoadingValidation && questions.length > 0 && messages.length === 0) {
      const firstQuestion = questions[0];
      const questionMessage: ChatMessage = {
        id: `question-${currentQuestionIndex}`,
        role: 'assistant',
        content: firstQuestion.content,
        timestamp: new Date()
      };
      setMessages([questionMessage]);

      // Show rating options for rating and mood questions after text animation
      if (firstQuestion.type === 'rating' || firstQuestion.type === 'mood') {
        const textAnimationDuration = calculateTextAnimationDuration(firstQuestion.content);
        setTimeout(() => {
          setShowRatingOptions(true);
        }, textAnimationDuration);
      }
    }
  }, [isLoadingValidation, questions, messages.length, currentQuestionIndex]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      requestAnimationFrame(() => {
        const scrollContainer = messagesEndRef.current?.closest('.overflow-y-auto');
        if (scrollContainer) {
          const scrollHeight = scrollContainer.scrollHeight;
          const clientHeight = scrollContainer.clientHeight;
          const currentScrollTop = scrollContainer.scrollTop;
          const newScrollTop = scrollHeight - clientHeight;
          
          if (currentScrollTop < newScrollTop - 50) {
            scrollContainer.scrollTo({
              top: newScrollTop,
              behavior: 'smooth'
            });
          }
        }
      });
    }
  }, [messages]);

  // Focus input when appropriate
  useEffect(() => {
    if (currentQuestion?.type === 'text' && inputRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current?.focus();
        }, TIMEOUTS.STEP_TRANSITION);
      });
    }
  }, [currentQuestion, currentQuestionIndex]);

  const handleRatingSelect = (rating: number) => {
    setSelectedRating(rating);
    setResponses(prev => ({
      ...prev,
      [currentQuestion.field]: rating
    }));

    // For mood questions, skip adding to chat history and go directly to next question
    if (currentQuestion.type === 'mood') {
      moveToNextQuestion();
      return;
    }

    // Add user message with rating (for non-mood questions)
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: `${rating}/10`,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    
    // Move to next question after delay
    setTimeout(() => {
      moveToNextQuestion();
    }, TIMEOUTS.RESPONSE_DELAY);
  };

  const handleTextSubmit = () => {
    if (!inputValue.trim()) return;

    // Save response
    const newResponses = {
      ...responses,
      [currentQuestion.field]: inputValue.trim()
    };
    setResponses(newResponses);
    
    console.log('💾 Saved response:', { 
      field: currentQuestion.field, 
      value: inputValue.trim(), 
      allResponses: newResponses 
    });

    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    
    // Special handling for morning or evening journal - generate AI response
    if (currentQuestion.id === 'morning-journal' || currentQuestion.id === 'evening-journal') {
      // Generate AI response after user's journal entry
      setTimeout(async () => {
        try {
          const aiResponse = await generateAIResponse(inputValue.trim(), newResponses);
          const aiMessage: ChatMessage = {
            id: `ai-${Date.now()}`,
            role: 'assistant',
            content: aiResponse,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiMessage]);
          
          // Continue journaling without adding another message
          setTimeout(() => {
            // Keep current question index to show text area again
            // No need to add empty message - just rely on existing state
          }, 1000);
        } catch (error) {
          console.error('Failed to generate AI response:', error);
          // Continue normally if AI response fails
          moveToNextQuestion(newResponses);
        }
      }, TIMEOUTS.RESPONSE_DELAY);
    } else {
      // Move to next question after delay, passing the updated responses
      setTimeout(() => {
        moveToNextQuestion(newResponses);
      }, TIMEOUTS.RESPONSE_DELAY);
    }
  };

  const moveToNextQuestion = (updatedResponses?: CheckInResponse) => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = questions[nextIndex];
      const currentQuestionObj = questions[currentQuestionIndex];
      
      setCurrentQuestionIndex(nextIndex);
      setShowRatingOptions(false);

      // Add next question message
      const questionMessage: ChatMessage = {
        id: `question-${nextIndex}`,
        role: 'assistant',
        content: nextQuestion.content,
        timestamp: new Date()
      };

      // If we're moving away from a mood question, replace it completely with next question
      if (currentQuestionObj.type === 'mood') {
        setMessages([questionMessage]); // Replace all messages with just the new question
      } else {
        setMessages(prev => [...prev, questionMessage]);
      }

      // Show rating options if it's a rating or mood question
      if (nextQuestion.type === 'rating' || nextQuestion.type === 'mood') {
        const textAnimationDuration = calculateTextAnimationDuration(nextQuestion.content);
        setTimeout(() => {
          setShowRatingOptions(true);
          // Reset selected rating for new question (only for rating questions, not mood)
          if (nextQuestion.type === 'rating') {
            const defaultRating = 5;
            setSelectedRating(defaultRating);
            setResponses(prev => ({
              ...prev,
              [nextQuestion.field]: defaultRating
            }));
          }
        }, textAnimationDuration);
      }
    } else {
      // Complete check-in with the latest responses
      handleSubmit(updatedResponses || responses);
    }
  };

  const handleSubmit = async (finalResponses?: CheckInResponse) => {
    setIsSubmitting(true);
    const responseTime = Math.floor((Date.now() - startTime) / 1000);
    const responsesToSubmit = finalResponses || responses;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showError('Authentication required', 'Please log in to continue');
        navigate('/signup');
        return;
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-auth-token': token
      };
      
      const accessGranted = localStorage.getItem('thinkmate_access_granted');
      if (accessGranted === 'true') {
        headers['x-access-granted'] = 'true';
      }

      const endpoint = checkInType === 'morning' ? 'morning' : 'evening';
      
      // Map fields correctly for each endpoint type
      let payload;
      if (checkInType === 'morning') {
        payload = {
          moodRating: responsesToSubmit.moodRating,
          potentialRating: responsesToSubmit.potentialRating, 
          primaryGoal: responsesToSubmit.primaryGoal,
          energyMotivation: responsesToSubmit.energyMotivation,
          responseTime
        };
      } else {
        payload = {
          ...responsesToSubmit,
          responseTime
        };
      }
      
      console.log('🔧 Submitting check-in:', { endpoint, payload });
      console.log('🔧 Raw responsesToSubmit:', responsesToSubmit);
      console.log('🔧 Individual payload fields:', {
        moodRating: responsesToSubmit.moodRating,
        potentialRating: responsesToSubmit.potentialRating, 
        primaryGoal: responsesToSubmit.primaryGoal,
        energyMotivation: responsesToSubmit.energyMotivation,
        responseTime
      });
      
      const response = await fetch(`${API_URL}/api/daily-checkins/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.msg || `Failed to submit ${checkInType} check-in`;
        
        // Handle specific error cases
        if (errorMessage.includes('already completed')) {
          showError(
            `${checkInType === 'morning' ? 'Morning' : 'Evening'} check-in already completed!`,
            `You've already completed your ${checkInType} check-in for today. Redirecting to dashboard...`
          );
          
          // Redirect to dashboard after a delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
          return;
        }
        
        throw new Error(errorMessage);
      }

      // Parse the successful response
      const data = await response.json();
      console.log('✅ Check-in API response:', data);

      const emoji = checkInType === 'morning' ? '🌅' : '🌙';
      
      // Create journal entry for the check-in to add to "Log" count
      try {
        const entryContent = createCheckInEntryContent(responsesToSubmit, checkInType, questions);
        await persistenceService.createEntry({
          date: new Date().toISOString().slice(0, 10),
          type: 'reflection',
          content: {
            title: `${checkInType === 'morning' ? 'Morning' : 'Evening'} Check-in - ${new Date().toLocaleDateString()}`,
            body: entryContent,
            questions: questions.map(q => q.content),
            responses: getFormattedResponses(responsesToSubmit, questions)
          },
          metadata: {
            wordCount: entryContent.split(/\s+/).length,
            tags: ['check-in', checkInType]
          }
        });
        console.log('✅ Check-in journal entry created');
      } catch (entryError) {
        console.error('Failed to create journal entry:', entryError);
        // Don't fail the whole check-in if entry creation fails
      }
      
      // For morning check-ins, show completion screen; for evening, go directly to dashboard
      if (checkInType === 'morning') {
        // Extract momentum days from response if available
        if (data.momentumDays) {
          setMomentumDays(data.momentumDays);
        }
        
        // Debug what we have available
        console.log('🔧 All responses:', responses);
        console.log('🔧 additionalThoughts:', responses.additionalThoughts);
        console.log('🔧 primaryGoal:', responses.primaryGoal);
        console.log('🔧 primaryGoal length:', responses.primaryGoal?.length);
        console.log('🔧 primaryGoal type:', typeof responses.primaryGoal);
        
        // Recreate the complete sentence that was shown in the chat
        let declaration = 'Focus on what matters most';
        if (responses.primaryGoal?.trim()) {
          // Get the same fill-in question template that was used
          const fillInTemplate = getMorningFillInQuestion();
          declaration = fillInTemplate.replace('___________', responses.primaryGoal.trim());
        } else if (responses.additionalThoughts?.trim()) {
          declaration = responses.additionalThoughts.trim();
        }
        
        console.log('🔧 Fill-in template:', getMorningFillInQuestion());
        console.log('🔧 Complete declaration:', declaration);
        console.log('🎉 Showing morning completion screen with declaration:', declaration);
        setShowCompletion(true);
      } else {
        showSuccess(
          `Evening check-in completed! ${emoji}`,
          'Great job staying consistent!'
        );
        navigate('/dashboard');
      }

    } catch (error) {
      console.error(`${checkInType} check-in submission error:`, error);
      showError(
        'Submission failed',
        error instanceof Error ? error.message : 'Failed to submit check-in'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  const shouldShowInput = () => {
    return currentQuestion?.type === 'text' && messages.length > 0 && !isSubmitting;
  };

  const shouldShowRating = () => {
    return currentQuestion?.type === 'rating' && showRatingOptions && !isSubmitting;
  };

  const shouldShowMood = () => {
    return currentQuestion?.type === 'mood' && showRatingOptions && !isSubmitting;
  };
  const shouldShowFillInBlank = () => {
    return currentQuestion?.type === 'fill-in-blank' && messages.length > 0 && !isSubmitting;
  };
  const shouldShowChoice = () => {
    return currentQuestion?.type === 'choice' && messages.length > 0 && !isSubmitting;
  };

  // Show loading screen while validating
  if (isLoadingValidation) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center space-y-6">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full p-4">
                <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900">Loading Check-in</h1>
            <p className="text-gray-600">
              Preparing your check-in questions...
            </p>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (isComplete) {
    return (
      <ErrorBoundary>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center space-y-6">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-4">
                <span className="text-3xl">✅</span>
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900">Check-in Complete!</h1>
            <p className="text-gray-600">
              Great job completing your {checkInType} check-in. Keep building that momentum!
            </p>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show completion screen if morning check-in is completed
  if (showCompletion && checkInType === 'morning') {
    const handleCompletionFinish = () => {
      navigate('/dashboard');
    };

    // Recreate the complete sentence for the completion screen
    let declaration = 'Focus on what matters most';
    if (responses.primaryGoal?.trim()) {
      const fillInTemplate = getMorningFillInQuestion();
      declaration = fillInTemplate.replace('___________', responses.primaryGoal.trim());
    } else if (responses.additionalThoughts?.trim()) {
      declaration = responses.additionalThoughts.trim();
    }

    return (
      <MorningCheckInComplete
        declaration={declaration}
        momentumDays={momentumDays}
        onComplete={handleCompletionFinish}
      />
    );
  }

  return (
    <ErrorBoundary>
      <div className="fixed inset-0 flex flex-col items-center z-50" style={{
        background: `
          radial-gradient(circle at 20% 20%, rgba(255, 243, 212, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(243, 240, 255, 0.3) 0%, transparent 50%),
          #FAFAFA
        `,
        fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}>
        <div className="w-full max-w-2xl h-full flex flex-col min-h-screen">

          {/* Header */}
          <div className="bg-white p-4 border-b border-gray-100">
            <div className="text-center">
              <div className="flex items-center justify-center mb-2">
                <div className="relative w-16 h-16">
                  {/* Breathing wave 1 */}
                  <div 
                    className="absolute inset-0 rounded-full animate-pulse"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.15))',
                      animation: 'breathe-slow 6s ease-in-out infinite',
                      transform: 'scale(1)',
                    }}
                  ></div>
                  
                  {/* Breathing wave 2 */}
                  <div 
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'linear-gradient(45deg, rgba(99, 102, 241, 0.12), rgba(139, 92, 246, 0.12))',
                      animation: 'breathe-gentle 8s ease-in-out infinite 2s',
                      transform: 'scale(0.8)',
                    }}
                  ></div>
                </div>
              </div>
              <h1 className="text-lg font-normal text-gray-900">
                {checkInType === 'morning' ? `Good Morning, ${getUserName()}!` : `Good Evening, ${getUserName()}!`}
              </h1>
              <p className="text-sm text-gray-600">
                {/* No subtitle text for either morning or evening */}
              </p>
            </div>
          </div>

          {/* Chat area */}
          <div className="flex-1 bg-white overflow-y-auto">
            <div className="max-w-3xl mx-auto p-4" style={{ paddingBottom: 'max(6rem, env(safe-area-inset-bottom) + 4rem)' }}>
              {messages.map((message, index) => {
                if (message.role === 'assistant') {
                  const isLastAssistantMessage = index === messages.length - 1;
                  const showInput = isLastAssistantMessage && shouldShowInput();
                  const showRating = isLastAssistantMessage && shouldShowRating();
                  const showMood = isLastAssistantMessage && shouldShowMood();
                  const showFillInBlank = isLastAssistantMessage && shouldShowFillInBlank();
                  const showChoice = isLastAssistantMessage && shouldShowChoice();
                  
                  return (
                    <div key={message.id}>
                      <div className="mb-6 w-full">
                        <div className={`message-content ${showInput || showRating || showMood || showFillInBlank || showChoice ? 'mb-1' : 'mb-4'}`} style={{ userSelect: 'text' }}>
                          <div className="whitespace-pre-wrap">
                            <AnimatedText 
                              content={message.content}
                              delay={200}
                              className="leading-relaxed"
                              style={{ color: '#111111' }}
                            />
                          </div>
                        </div>
                        
                        {showInput && (
                          <div className="mt-0">
                            <textarea
                              ref={inputRef}
                              value={inputValue}
                              onChange={(e) => {
                                const truncatedValue = e.target.value.slice(0, LIMITS.INPUT_MAX_LENGTH);
                                setInputValue(truncatedValue);
                              }}
                              onKeyDown={handleKeyDown}
                              placeholder={currentQuestion.placeholder || 'Write...'}
                              className="w-full min-h-[120px] py-4 pl-0 pr-4 border-none bg-transparent focus:outline-none focus:ring-0 resize-none font-normal leading-relaxed"
                              style={{ 
                                color: '#111111', 
                                fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                                minHeight: '120px',
                                height: 'auto'
                              }}
                              rows={6}
                              autoFocus
                              onInput={(e) => {
                                const target = e.target as HTMLTextAreaElement;
                                target.style.height = 'auto';
                                target.style.height = `${Math.max(120, target.scrollHeight)}px`;
                              }}
                            />
                            {/* Show choice buttons for morning or evening journal */}
                            {(currentQuestion.id === 'morning-journal' || currentQuestion.id === 'evening-journal') ? (
                              <div className="mt-6 space-y-4">
                                <div className="flex justify-between">
                                  <button
                                    onClick={() => {
                                      // Submit current text if any, then continue journaling
                                      if (inputValue.trim()) {
                                        handleTextSubmit();
                                      }
                                      // If no text, buttons remain available for user to type and try again
                                    }}
                                    disabled={isSubmitting}
                                    className="w-1/3 py-3 px-4 rounded-2xl font-semibold text-base transition-all duration-200 bg-gradient-to-r from-pink-500 to-blue-500 text-white hover:from-pink-600 hover:to-blue-600 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Push Further
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Submit current text if any, then complete
                                      if (inputValue.trim()) {
                                        // Save the current input and complete
                                        const newResponses = {
                                          ...responses,
                                          [currentQuestion.field]: inputValue.trim()
                                        };
                                        setResponses(newResponses);
                                        handleSubmit(newResponses);
                                      } else {
                                        handleSubmit();
                                      }
                                    }}
                                    disabled={isSubmitting}
                                    className="w-1/3 py-3 px-4 rounded-2xl font-semibold text-base transition-all duration-200 bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Finish Log
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Regular continue button for other text questions */
                              inputValue.trim() && (
                                <div className="mt-2">
                                  <button
                                    onClick={handleTextSubmit}
                                    disabled={!inputValue.trim() || isSubmitting}
                                    className={`px-6 py-3 rounded-3xl font-semibold transition-all duration-200 ${
                                      inputValue.trim() 
                                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white' 
                                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    } disabled:opacity-50 disabled:cursor-not-allowed shadow-lg`}
                                  >
                                    Continue
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {showRating && (
                          <div className="mt-0">
                            {/* Use MoodSlider for evening progress rating */}
                            {checkInType === 'evening' && currentQuestion.id === 'evening-progress' ? (
                              <MoodSlider
                                onContinue={(moodValue) => {
                                  // Save the mood value
                                  setSelectedRating(moodValue);
                                  setResponses(prev => ({
                                    ...prev,
                                    [currentQuestion.field]: moodValue
                                  }));
                                  
                                  // Hide rating options immediately
                                  setShowRatingOptions(false);
                                  
                                  // For mood slider, skip adding user message and clear chat history
                                  // to show only the next question (single-question mode)
                                  setTimeout(() => {
                                    // Clear all previous messages for clean single-question interface
                                    setMessages([]);
                                    moveToNextQuestion();
                                  }, 300); // Shorter delay for smoother transition
                                }}
                                disabled={isSubmitting}
                                className="py-4"
                              />
                            ) : (
                              /* Default rating buttons for other questions */
                              <>
                                <div className="flex justify-between items-center text-sm text-gray-500 px-2 mb-4">
                                  <span>{currentQuestion.lowLabel}</span>
                                  <span>{currentQuestion.highLabel}</span>
                                </div>
                                <div className="flex justify-center space-x-2 flex-wrap">
                                  {Array.from({ length: currentQuestion.max - currentQuestion.min + 1 }, (_, i) => {
                                    const rating = currentQuestion.min + i;
                                    return (
                                      <button
                                        key={rating}
                                        onClick={() => handleRatingSelect(rating)}
                                        className={`w-12 h-12 rounded-full border-2 text-sm font-semibold transition-all duration-200 ${
                                          selectedRating === rating
                                            ? `${checkInType === 'morning' 
                                                ? 'bg-gradient-to-r from-orange-500 to-yellow-500 border-orange-500' 
                                                : 'bg-gradient-to-r from-indigo-500 to-purple-500 border-indigo-500'
                                              } text-white shadow-lg scale-110`
                                            : `border-gray-300 text-gray-600 hover:${checkInType === 'morning' ? 'border-orange-300 hover:bg-orange-50' : 'border-indigo-300 hover:bg-indigo-50'}`
                                        }`}
                                      >
                                        {rating}
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {showMood && (
                          <div className="mt-0">
                            <MoodSlider
                              onContinue={(moodValue) => handleRatingSelect(moodValue)}
                              disabled={isSubmitting}
                            />
                          </div>
                        )}

                        {showFillInBlank && (
                          <div className="mt-0 space-y-4">
                            {/* Morning fill-in-the-blank */}
                            {checkInType === 'morning' && currentQuestion.id === 'morning-goal' ? (
                              <>
                                <div className="text-base leading-relaxed">
                                  <div>
                                    <span>{currentQuestion.fillInText?.replace('___________', '')}</span>
                                    <span className="relative inline-block">
                                      <input
                                        type="text"
                                        value={responses.primaryGoal || ''}
                                        onChange={(e) => setResponses(prev => ({ ...prev, primaryGoal: e.target.value }))}
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && responses.primaryGoal?.trim()) {
                                            e.preventDefault();
                                            const primaryGoal = responses.primaryGoal?.trim();
                                            if (!primaryGoal) return;

                                            // Add user message showing response with the current question text
                                            const questionText = currentQuestion.fillInText?.replace('___________', primaryGoal) || `${primaryGoal}`;
                                            const userMessage: ChatMessage = {
                                              id: `user-${Date.now()}`,
                                              role: 'user',
                                              content: questionText,
                                              timestamp: new Date()
                                            };
                                            setMessages(prev => [...prev, userMessage]);

                                            // Move to next question after delay
                                            setTimeout(() => {
                                              moveToNextQuestion();
                                            }, TIMEOUTS.RESPONSE_DELAY);
                                          }
                                        }}
                                        placeholder=""
                                        className="bg-transparent border-0 outline-none text-base ml-1"
                                        style={{ 
                                          borderBottom: `2px solid ${responses.primaryGoal?.length ? '#f97316' : '#d1d5db'}`,
                                          paddingBottom: '2px',
                                          width: `${Math.max(50, (responses.primaryGoal || '').length * 8 + 8)}px`,
                                          minWidth: '50px'
                                        }}
                                      />
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Submit button - only show when user has typed something */}
                                {responses.primaryGoal?.trim() && (
                                  <div className="flex justify-start pt-4">
                                    <button
                                      onClick={() => {
                                        const primaryGoal = responses.primaryGoal?.trim();
                                        
                                        if (!primaryGoal) {
                                          return; // Don't submit if field is empty
                                        }

                                        // Add user message showing response with the current question text
                                        const questionText = currentQuestion.fillInText?.replace('___________', primaryGoal) || `${primaryGoal}`;
                                        const userMessage: ChatMessage = {
                                          id: `user-${Date.now()}`,
                                          role: 'user',
                                          content: questionText,
                                          timestamp: new Date()
                                        };
                                        setMessages(prev => [...prev, userMessage]);

                                        // Move to next question after delay
                                        setTimeout(() => {
                                          moveToNextQuestion();
                                        }, TIMEOUTS.RESPONSE_DELAY);
                                      }}
                                      disabled={isSubmitting || !responses.primaryGoal?.trim()}
                                      className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg`}
                                    >
                                      Continue
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              /* Evening fill-in-the-blank (existing code) */
                              <>
                                <div className="text-base leading-relaxed">
                                  <span>My main goal today went </span>
                                  <input
                                    type="text"
                                    value={responses.goalOutcome || ''}
                                    onChange={(e) => setResponses(prev => ({ ...prev, goalOutcome: e.target.value }))}
                                    placeholder="..."
                                    className="inline-block min-w-[120px] max-w-[200px] px-2 py-1 border-b-2 border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent text-base"
                                    style={{ 
                                      width: `${Math.max(120, (responses.goalOutcome?.length || 3) * 12)}px`,
                                      borderTop: 'none',
                                      borderLeft: 'none',
                                      borderRight: 'none'
                                    }}
                                    disabled={isSubmitting}
                                  />
                                  <span> and tomorrow&apos;s priority is </span>
                                  <input
                                    type="text"
                                    value={responses.tomorrowPriority || ''}
                                    onChange={(e) => setResponses(prev => ({ ...prev, tomorrowPriority: e.target.value }))}
                                    placeholder="..."
                                    className="inline-block min-w-[120px] max-w-[200px] px-2 py-1 border-b-2 border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent text-base"
                                    style={{ 
                                      width: `${Math.max(120, (responses.tomorrowPriority?.length || 3) * 12)}px`,
                                      borderTop: 'none',
                                      borderLeft: 'none',
                                      borderRight: 'none'
                                    }}
                                    disabled={isSubmitting}
                                  />
                                </div>

                                {/* Submit button - only show when user has typed in both fields */}
                                {(responses.goalOutcome?.trim() || responses.tomorrowPriority?.trim()) && (
                                  <div className="flex justify-end pt-4">
                                    <button
                                      onClick={() => {
                                        const goalOutcome = responses.goalOutcome?.trim();
                                        const tomorrowPriority = responses.tomorrowPriority?.trim();
                                        
                                        if (!goalOutcome || !tomorrowPriority) {
                                          return; // Don't submit if either field is empty
                                        }

                                        // Add user message showing both responses
                                        const userMessage: ChatMessage = {
                                          id: `user-${Date.now()}`,
                                          role: 'user',
                                          content: `My main goal today went ${goalOutcome} and tomorrow's priority is ${tomorrowPriority}`,
                                          timestamp: new Date()
                                        };
                                        setMessages(prev => [...prev, userMessage]);

                                        // Move to next question after delay
                                        setTimeout(() => {
                                          moveToNextQuestion();
                                        }, TIMEOUTS.RESPONSE_DELAY);
                                      }}
                                      disabled={isSubmitting || !responses.goalOutcome?.trim() || !responses.tomorrowPriority?.trim()}
                                      className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg`}
                                    >
                                      Continue
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {showChoice && (
                          <div className="mt-4 space-y-4">
                            <div className="flex justify-between">
                              {currentQuestion.choices?.map((choice) => (
                                <button
                                  key={choice.id}
                                  onClick={() => {
                                    if (choice.action === 'complete') {
                                      // Finish Log - complete the check-in
                                      handleSubmit();
                                    } else {
                                      // Push Further - continue to journaling (skip adding user message)
                                      setTimeout(() => {
                                        moveToNextQuestion();
                                      }, 300); // Shorter delay for smoother transition
                                    }
                                  }}
                                  disabled={isSubmitting}
                                  className={`w-1/3 py-3 px-4 rounded-2xl font-semibold text-base transition-all duration-200 ${
                                    choice.id === 'push-further'
                                      ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white hover:from-pink-600 hover:to-blue-600 shadow-lg'
                                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-lg'
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {choice.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                
                // User messages
                if (message.role === 'user') {
                  const lines = message.content.split(/\r?\n/);
                  return (
                    <div
                      key={message.id}
                      className="mb-4 w-full"
                    >
                      <div className="message-content" style={{ userSelect: 'text', color: '#111111' }}>
                        <div className="whitespace-pre-wrap">
                          {lines.map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
              
              <div ref={messagesEndRef} />
              
              {isSubmitting && (
                <div className="flex justify-start mb-4">
                  <div className="bg-gray-100 rounded-lg px-4 py-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Skip option */}
          <div className="bg-white p-4 border-t border-gray-100 text-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 text-sm hover:text-gray-700 transition-colors"
            >
              Skip for now and return to dashboard
            </button>
          </div>
        </div>

        {/* Breathing animation keyframes */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes breathe-slow {
              0%, 100% {
                transform: scale(1);
                opacity: 0.15;
              }
              50% {
                transform: scale(1.6);
                opacity: 0.05;
              }
            }

            @keyframes breathe-gentle {
              0%, 100% {
                transform: scale(0.8);
                opacity: 0.12;
              }
              50% {
                transform: scale(1.4);
                opacity: 0.06;
              }
            }
          `
        }} />
      </div>
    </ErrorBoundary>
  );
};

export default CheckInChat;